import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Space,
  Input,
  Form,
  Button,
  notification,
  Row,
  Col,
  Typography,
  Spin,
  Table,
  message,
  Tooltip,
  Switch,
  Tag,
  Tabs,
} from "antd";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { CopyOutlined, EyeOutlined, SearchOutlined, ClearOutlined } from "@ant-design/icons";
import moment from "moment";
import RequestView from "../../shared/RequestView";
import RequestViewModal from "../../Request/RequestViewModal";
import { ReactComponent as AiStars } from "../../../assets/svg/ai-creation.svg";

const { TextArea } = Input;
const { Text } = Typography;

// Add interface for tag distribution
interface TagDistribution {
  tag: string;
  count: number;
}

const DatasetEdit: React.FC = () => {
  const { id } = useParams();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);

  // Added requests state
  const [currentAddedPage, setCurrentAddedPage] = useState<number>(1);
  const [addedPageSize, setAddedPageSize] = useState<number>(10);
  const [addedRequests, setAddedRequests] = useState<ChatCompletionRequest[]>(
    []
  );
  const [totalAdded, setTotalAdded] = useState<number>(0);
  const [isAddedLoading, setIsAddedLoading] = useState<boolean>(false);
  const addedRequestsCacheRef = useRef<{
    [page: number]: ChatCompletionRequest[];
  }>({});
  const [selectedAddedRequests, setSelectedAddedRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [showOnlySelectedAdded, setShowOnlySelectedAdded] = useState(false);

  // Eligible requests state
  const [currentEligiblePage, setCurrentEligiblePage] = useState<number>(1);
  const [eligiblePageSize, setEligiblePageSize] = useState<number>(10);
  const [eligibleRequests, setEligibleRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [totalEligible, setTotalEligible] = useState<number>(0);
  const [isEligibleLoading, setIsEligibleLoading] = useState<boolean>(false);
  const eligibleRequestsCacheRef = useRef<{
    [page: number]: ChatCompletionRequest[];
  }>({});
  const [selectedEligibleRequests, setSelectedEligibleRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [showOnlySelectedEligible, setShowOnlySelectedEligible] =
    useState(false);

  const [tagDistribution, setTagDistribution] = useState<TagDistribution[]>([]);
  const [showAllTags, setShowAllTags] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("added");
  const [sessionIdFilter, setSessionIdFilter] = useState<string>("");
  const [appliedSessionIdFilter, setAppliedSessionIdFilter] = useState<string>("");
  const [queryFilter, setQueryFilter] = useState<string>("");
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ChatCompletionRequest[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [eligibleRefreshTrigger, setEligibleRefreshTrigger] = useState<number>(0);
  
  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ChatCompletionRequest | null>(null);

  // Add the missing handler functions
  const handleViewRequest = (request: ChatCompletionRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  // Fetch dataset details
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      DatasetService.getDataset(Number(id), setIsLoading, apiKey).subscribe(
        (data: FinetuneDataset) => {
          if (data) {
            setDataset(data);
            form.setFieldsValue({
              name: data.name,
              description: data.description,
            });
          } else {
            notification.error({
              message: "Error",
              description: "Failed to fetch dataset: Empty response received",
            });
          }
        },
        (error: any) => {
          console.error("Error fetching dataset:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch dataset: ${error}`,
          });
        }
      );
    }
  }, [id, apiKey, form]);

  // Fetch dataset requests (Added tab)
  useEffect(() => {
    if (id) {
      fetchAddedRequests(currentAddedPage);
    }
  }, [currentAddedPage, id, addedPageSize]);

  // Fetch eligible requests (Eligible tab)
  useEffect(() => {
    if (id && activeTab === "eligible") {
      fetchEligibleRequests(currentEligiblePage);
    }
  }, [currentEligiblePage, id, eligiblePageSize, activeTab, eligibleRefreshTrigger]);

  // Fetch tag distribution
  useEffect(() => {
    if (id) {
      DatasetService.getDatasetRequestDistribution(id, null, apiKey).subscribe(
        (response: TagDistribution[]) => {
          setTagDistribution(response);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch tag distribution: ${error}`,
          });
        }
      );
    }
  }, [id]);

  const fetchAddedRequests = (page: number) => {
    if (addedRequestsCacheRef.current[page]) {
      setAddedRequests(addedRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * addedPageSize;
    setIsAddedLoading(true);

    DatasetService.getDatasetRequests(
      Number(id),
      offset,
      addedPageSize,
      setIsAddedLoading,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setAddedRequests(response.items);
        setTotalAdded(response.total);
        addedRequestsCacheRef.current = {
          ...addedRequestsCacheRef.current,
          [page]: response.items,
        };
        setIsAddedLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsAddedLoading(false);
      }
    );
  };

  const fetchEligibleRequests = (page: number) => {
    if (eligibleRequestsCacheRef.current[page] && !sessionIdFilter) {
      setEligibleRequests(eligibleRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * eligiblePageSize;
    setIsEligibleLoading(true);

    DatasetService.getEligibleDatasetRequests(
      Number(id),
      offset,
      eligiblePageSize,
      setIsEligibleLoading,
      apiKey,
      sessionIdFilter
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setEligibleRequests(response.items);
        setTotalEligible(response.total);

        // Only cache if no session filter is applied
        if (!sessionIdFilter) {
          eligibleRequestsCacheRef.current = {
            ...eligibleRequestsCacheRef.current,
            [page]: response.items,
          };
        }

        setIsEligibleLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch eligible requests: ${error}`,
        });
        setIsEligibleLoading(false);
      }
    );
  };

  const handleSearch = () => {
    // Reset to first page when searching
    setCurrentEligiblePage(1);
    fetchEligibleRequests(1);
  };

  const handleAddSelectedRequests = () => {
    // This is a placeholder for future implementation
    message.info(
      `Adding ${selectedEligibleRequests.length} requests to the dataset`
    );
  };

  const handleRemoveSelectedRequests = () => {
    // This is a placeholder for future implementation
    message.info(
      `Removing ${selectedAddedRequests.length} requests from the dataset`
    );
  };

  const handleQuerySearch = () => {
    if (!queryFilter.trim()) {
      // If empty query, reset to normal mode
      clearSearch();
      return;
    }

    // Clear session filter when doing a query search
    setSessionIdFilter("");
    setEligibleRefreshTrigger((prev) => prev + 1);
    
    setIsSearchLoading(true);
    setIsSearchMode(true);

    DatasetService.searchEligibleDatasetRequests(
      Number(id),
      queryFilter,
      null,
      apiKey
    ).subscribe(
      (items: ChatCompletionRequest[]) => {
        const updatedItems = items.map((request) => {
          const selectedVersion = selectedEligibleRequests.find(
            (selected) => selected.request_id === request.request_id
          );
          return selectedVersion || request;
        });
        
        setSearchResults(updatedItems);
        
        // Reset to first page when searching
        setCurrentEligiblePage(1);
        
        // Calculate the items for the current page
        const startIndex = 0;
        const endIndex = Math.min(eligiblePageSize, updatedItems.length);
        setEligibleRequests(updatedItems.slice(startIndex, endIndex));
        
        setTotalEligible(updatedItems.length);
        setIsSearchLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to search requests: ${error}`,
        });
        setIsSearchLoading(false);
      }
    );
  };

  const clearSearch = () => {
    setQueryFilter("");
    setIsSearchMode(false);
    setSearchResults([]);
    setCurrentEligiblePage(1);
    eligibleRequestsCacheRef.current = {};
    setEligibleRefreshTrigger((prev) => prev + 1);
    
    // Also clear session filter if it exists
    if (sessionIdFilter) {
      setSessionIdFilter("");
    }
  };

  // Add this function to handle pagination of search results
  const handleSearchPagination = (page: number, pageSize: number) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, searchResults.length);
    setEligibleRequests(searchResults.slice(startIndex, endIndex));
    setCurrentEligiblePage(page);
  };

  const addedColumns = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "start" }}>
          Tags
          <AiStars
            style={{ width: "12px", height: "12px", color: "#8c8c8c" }}
          />
        </div>
      ),
      key: "tags",
      width: "40%",
      render: (_: any, record: ChatCompletionRequest) => {
        try {
          // First check for dataset-specific tags
          let tags: string[] = [];
          if (record.metadata?._maitai_dataset_request_meta) {
            const meta = JSON.parse(
              record.metadata._maitai_dataset_request_meta as string
            );
            tags = meta.tags || [];
          }

          // If no dataset-specific tags, fall back to request metadata tags
          if (tags.length === 0 && record.metadata?._maitai_request_meta) {
            const metaData = JSON.parse(
              record.metadata._maitai_request_meta as string
            );
            tags = metaData.tags || [];
          }

          return (
            <Space wrap>
              {tags.map((tag: string) => (
                <Tag key={tag} bordered={true}>
                  {tag}
                </Tag>
              ))}
              {tags.length === 0 && (
                <span style={{ color: "#999" }}>No tags</span>
              )}
            </Space>
          );
        } catch (e) {
          console.error("Error parsing metadata:", e);
          return <span style={{ color: "#999" }}>No tags</span>;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "25%",
      render: (_: any, record: ChatCompletionRequest) => {
        const requestId = record.request_id || "";
        const truncatedId = `...${requestId.slice(-8)}`;
        return (
          <Space style={{ width: "100%" }}>
            {truncatedId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_: any, record: ChatCompletionRequest) => (
        <Space>
          <Tooltip
            title={
              <RequestView
                request={record}
                apiKey={apiKey}
                isEditable={false}
              />
            }
            placement="left"
            overlayStyle={{ maxWidth: "600px", width: "600px" }}>
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              onClick={() => handleViewRequest(record)}>
              View
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const eligibleColumns = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "start" }}>
          Tags
          <AiStars
            style={{ width: "12px", height: "12px", color: "#8c8c8c" }}
          />
        </div>
      ),
      key: "tags",
      width: "40%",
      render: (_: any, record: ChatCompletionRequest) => {
        try {
          if (record.metadata?._maitai_request_meta) {
            const metaData = JSON.parse(
              record.metadata._maitai_request_meta as string
            );
            const tags = metaData.tags || [];
            return (
              <Space wrap>
                {tags.map((tag: string) => (
                  <Tag key={tag} bordered={true}>
                    {tag}
                  </Tag>
                ))}
                {tags.length === 0 && (
                  <span style={{ color: "#999" }}>No tags</span>
                )}
              </Space>
            );
          }
          return <span style={{ color: "#999" }}>No tags</span>;
        } catch (e) {
          console.error("Error parsing metadata:", e);
          return <span style={{ color: "#999" }}>No tags</span>;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "25%",
      render: (_: any, record: ChatCompletionRequest) => {
        const requestId = record.request_id || "";
        const truncatedId = `...${requestId.slice(-8)}`;
        return (
          <Space style={{ width: "100%" }}>
            {truncatedId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_: any, record: ChatCompletionRequest) => (
        <Space>
          <Tooltip
            title={
              <RequestView
                request={record}
                apiKey={apiKey}
                isEditable={false}
              />
            }
            placement="left"
            overlayStyle={{ maxWidth: "600px", width: "600px" }}>
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              onClick={() => handleViewRequest(record)}>
              View
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);

      const payload = {
        name: values.name,
        description: values.description,
      };

      DatasetService.updateDataset(
        Number(id),
        payload,
        setIsSaving,
        apiKey
      ).subscribe(
        () => {
          notification.success({
            message: "Success",
            description: "Dataset updated successfully",
          });
          navigate(`/models/datasets/${id}`);
        },
        (error: any) => {
          console.error("Error updating dataset:", error);
          notification.error({
            message: "Error",
            description: `Failed to update dataset: ${error}`,
          });
          setIsSaving(false);
        }
      );
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const items = [
    {
      key: "eligible",
      label: "Eligible Requests",
      children: (
        <>
          <div style={{ display: "flex", gap: "8px", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <Space.Compact style={{ width: "300px" }}>
                  <Input
                    placeholder="Search by query..."
                    value={queryFilter}
                    onChange={(e) => {
                      setQueryFilter(e.target.value);
                      if (e.target.value === "") {
                        clearSearch();
                      }
                    }}
                    onPressEnter={handleQuerySearch}
                    allowClear
                    disabled={isSearchLoading}
                  />
                  <Button 
                    type="primary" 
                    onClick={handleQuerySearch}
                    loading={isSearchLoading}
                    disabled={!queryFilter.trim()}
                  >
                    <SearchOutlined />
                  </Button>
                </Space.Compact>
                
                <Space.Compact style={{ width: "300px" }}>
                  <Input
                    placeholder="Search for session..."
                    value={sessionIdFilter}
                    onChange={(e) => {
                      setSessionIdFilter(e.target.value);
                      if (e.target.value === "" && appliedSessionIdFilter) {
                        // If the input is cleared and there was a filter applied
                        setAppliedSessionIdFilter("");
                        setCurrentEligiblePage(1);
                        eligibleRequestsCacheRef.current = {};
                        setEligibleRefreshTrigger((prev) => prev + 1);
                      }
                    }}
                    onPressEnter={handleSearch}
                    allowClear
                    disabled={isSearchMode || isSearchLoading}
                  />
                  <Button 
                    type="primary" 
                    onClick={handleSearch}
                    disabled={isSearchMode || isSearchLoading || !sessionIdFilter.trim()}
                  >
                    <SearchOutlined />
                  </Button>
                </Space.Compact>
                
                {(isSearchMode || appliedSessionIdFilter) && (
                  <Button 
                    onClick={clearSearch} 
                    icon={<ClearOutlined />}
                    disabled={isSearchLoading}
                  >
                    Clear Search
                  </Button>
                )}
              </div>
              
              {isSearchMode && searchResults.length > 0 && (
                <div style={{ marginTop: "4px" }}>
                  <Tag color="blue">
                    Found {searchResults.length} results for query: "{queryFilter}"
                  </Tag>
                </div>
              )}
              
              {appliedSessionIdFilter && !isSearchMode && (
                <div style={{ marginTop: "4px" }}>
                  <Tag color="blue">
                    Filtering by session: "{appliedSessionIdFilter}"
                  </Tag>
                </div>
              )}
            </div>
            
            {selectedEligibleRequests.length > 0 && (
              <Button
                type="primary"
                onClick={handleAddSelectedRequests}
                loading={isEligibleLoading}
                disabled={isEligibleLoading}>
                Add Selected ({selectedEligibleRequests.length})
              </Button>
            )}
          </div>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Switch
                checked={showOnlySelectedEligible}
                onChange={setShowOnlySelectedEligible}
                style={{ marginRight: 8 }}
              />
              <span>Show Only Selected</span>
            </div>
          </div>
          <Table
            rowKey="request_id"
            columns={eligibleColumns}
            dataSource={
              showOnlySelectedEligible
                ? selectedEligibleRequests
                : eligibleRequests
            }
            loading={isEligibleLoading || isSearchLoading}
            pagination={{
              current: currentEligiblePage,
              pageSize: eligiblePageSize,
              total: showOnlySelectedEligible
                ? selectedEligibleRequests.length
                : (isSearchMode ? searchResults.length : totalEligible),
              onChange: (page, pageSize) => {
                if (isSearchMode) {
                  // In search mode, handle pagination differently
                  handleSearchPagination(page, pageSize || eligiblePageSize);
                  return;
                }
                
                setCurrentEligiblePage(page);
                if (pageSize && pageSize !== eligiblePageSize) {
                  setEligiblePageSize(pageSize);
                  setEligibleRequests([]);
                  eligibleRequestsCacheRef.current = {};
                }
              },
              showSizeChanger: !isSearchMode,
              pageSizeOptions: ["10", "20", "50", "100"],
              onShowSizeChange: (current, size) => {
                if (isSearchMode) return;
                
                setCurrentEligiblePage(1);
                setEligiblePageSize(size);
                setEligibleRequests([]);
                eligibleRequestsCacheRef.current = {};
              },
            }}
            rowSelection={{
              selectedRowKeys: selectedEligibleRequests.map(
                (r) => r.request_id
              ),
              onChange: (_, selectedRows) => {
                setSelectedEligibleRequests(selectedRows);
              },
            }}
          />
        </>
      ),
    },
    {
      key: "added",
      label: "Added Requests",
      children: (
        <>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            <div>
              {selectedAddedRequests.length > 0 && (
                <Button
                  type="primary"
                  danger
                  onClick={handleRemoveSelectedRequests}
                  loading={isAddedLoading}
                  disabled={isAddedLoading}>
                  Remove Selected ({selectedAddedRequests.length})
                </Button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Switch
                checked={showOnlySelectedAdded}
                onChange={setShowOnlySelectedAdded}
                style={{ marginRight: 8 }}
              />
              <span>Show Only Selected</span>
            </div>
          </div>
          <Table
            rowKey="request_id"
            columns={addedColumns}
            dataSource={
              showOnlySelectedAdded ? selectedAddedRequests : addedRequests
            }
            loading={isAddedLoading}
            pagination={{
              current: currentAddedPage,
              pageSize: addedPageSize,
              total: showOnlySelectedAdded
                ? selectedAddedRequests.length
                : totalAdded,
              onChange: (page, pageSize) => {
                setCurrentAddedPage(page);
                if (pageSize) {
                  setAddedPageSize(pageSize);
                  setAddedRequests([]);
                  addedRequestsCacheRef.current = {};
                }
              },
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onShowSizeChange: (current, size) => {
                setCurrentAddedPage(1);
                setAddedPageSize(size);
                setAddedRequests([]);
                addedRequestsCacheRef.current = {};
              },
            }}
            rowSelection={{
              selectedRowKeys: selectedAddedRequests.map((r) => r.request_id),
              onChange: (_, selectedRows) => {
                setSelectedAddedRequests(selectedRows);
              },
            }}
          />
        </>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div
          style={{ fontSize: "24px", fontWeight: "600", marginBottom: "24px" }}>
          {!dataset ? (
            <Spin size="small" />
          ) : (
            <>
              <Link to={"/models/datasets"} style={{ color: "#828282" }}>
                Datasets
                {" / "}
              </Link>
              <Link to={`/models/datasets/${id}`} style={{ color: "#828282" }}>
                {dataset.name}
                {" / "}
              </Link>
              Edit
            </>
          )}
        </div>

        {dataset && (
          <Card
            size="small"
            title={
              <Text strong style={{ fontSize: "16px" }}>
                Details
              </Text>
            }
            style={{
              background: "#f8f9fa",
              borderRadius: "8px",
              marginBottom: "12px",
            }}
            loading={isLoading}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text type="secondary">Source</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {dataset.intent_group_name &&
                    !dataset.application_id &&
                    !dataset.intent_id ? (
                      <Link to={`/intent-groups/${dataset.intent_group_id}`}>
                        {dataset.intent_group_name}
                      </Link>
                    ) : dataset.application_name &&
                      dataset.intent_name &&
                      !dataset.intent_group_id ? (
                      <>
                        <Link to={`/application/${dataset.application_id}`}>
                          {dataset.application_name}
                        </Link>
                        {" / "}
                        <Link
                          to={`/application/${dataset.application_id}/intent/${dataset.intent_id}`}>
                          {dataset.intent_name}
                        </Link>
                      </>
                    ) : (
                      "Unknown source"
                    )}
                  </Text>
                </div>
              </Col>
              <Col span={6}>
                <Text type="secondary">Date Created</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {moment(dataset.date_created).format(
                      "MMM D, YYYY HH:mm:ss"
                    )}
                  </Text>
                </div>
              </Col>
              <Col span={6}>
                <Text type="secondary">Total Requests</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {dataset.request_count || 0}
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
        )}

        <Form form={form} layout="vertical" disabled={isLoading}>
          <div
            style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
            <Form.Item
              name="name"
              label={<span style={{ fontWeight: 600 }}>Name</span>}
              rules={[{ required: true, message: "Please enter a name" }]}
              style={{ flex: 1 }}>
              <Input placeholder="Enter dataset name" />
            </Form.Item>
          </div>

          <Form.Item
            name="description"
            label={<span style={{ fontWeight: 600 }}>Description</span>}>
            <TextArea placeholder="Enter dataset description" rows={4} />
          </Form.Item>

          {tagDistribution.length > 0 && (
            <Form.Item
              label={
                <span style={{ fontWeight: 600 }}>Request Distribution</span>
              }>
              <Space wrap>
                {(showAllTags ? tagDistribution : tagDistribution.slice(0, 20)).map(({ tag, count }) => (
                  <Tag key={tag} bordered={true}>
                    {tag} ({count})
                  </Tag>
                ))}
                {tagDistribution.length > 20 && !showAllTags && (
                  <Button 
                    type="link" 
                    onClick={() => setShowAllTags(true)}
                    style={{ padding: "0 8px" }}
                  >
                    Show All ({tagDistribution.length})
                  </Button>
                )}
                {showAllTags && tagDistribution.length > 20 && (
                  <Button 
                    type="link" 
                    onClick={() => setShowAllTags(false)}
                    style={{ padding: "0 8px" }}
                  >
                    Show Less
                  </Button>
                )}
              </Space>
            </Form.Item>
          )}
        </Form>

        <Tabs items={items} activeKey={activeTab} onChange={setActiveTab} />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "24px",
          }}>
          <Button
            onClick={() => navigate(`/models/datasets/${id}`)}
            disabled={isSaving}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave} loading={isSaving}>
            Save Changes
          </Button>
        </div>
      </Space>
      
      {selectedRequest && (
        <RequestViewModal
          request={selectedRequest}
          isVisible={isModalVisible}
          onClose={handleCloseModal}
          apiKey={apiKey}
        />
      )}
    </Card>
  );
};

export default DatasetEdit;
